import React, { useRef, useState } from "react";
import { Upload, X, Loader2, Image as ImageIcon, FolderOpen, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import { resolveAsset } from "../store/useSiteContent";
import { ImageLibraryModal, MEDIA_GROUPS } from "./ImageLibraryModal";

const MAX_BYTES = 8 * 1024 * 1024;
const HEIC_RX = /\.(heic|heif)$/i;

/**
 * Shared image picker: Upload · Choose existing · Replace. Progress, clear errors with Retry, the previous image is
 * kept until a replacement succeeds, HEIC is explained before upload, alt text + focal point are stored on the asset.
 * Props: value (url), onChange(url, meta?), label, testid, group (default media group), showFocal (crop previews).
 */
export default function ImageUploader({ value, onChange, label = "Image", testid = "image-uploader", group = "other", showFocal = true }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [lastFile, setLastFile] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [library, setLibrary] = useState(false);
  const [meta, setMeta] = useState(null); // {id, alt, focal_x, focal_y}
  const [altDraft, setAltDraft] = useState("");

  const previewUrl = resolveAsset(value, "");
  const mediaId = /\/api\/media\/([a-f0-9-]+)/i.exec(value || "")?.[1] || null;

  const loadMeta = async (id) => {
    if (!id) { setMeta(null); return; }
    try {
      const r = await api.get("/admin/media", { params: { q: "" } });
      const m = (r.data || []).find((x) => x.id === id);
      if (m) { setMeta({ id, alt: m.alt || "", focal_x: m.focal_x ?? 50, focal_y: m.focal_y ?? 50 }); setAltDraft(m.alt || ""); }
    } catch { /* meta is optional */ }
  };
  React.useEffect(() => { loadMeta(mediaId); /* eslint-disable-next-line */ }, [mediaId]);

  const upload = async (file) => {
    if (!file) return;
    setError("");
    setLastFile(null);
    if (HEIC_RX.test(file.name) || /heic|heif/i.test(file.type)) {
      setError("This is an iPhone HEIC photo , the website can't use it yet. On the phone: Settings → Camera → Formats → Most Compatible, or share it as JPEG, then try again. Your current image is unchanged.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`That file is ${(file.size / 1024 / 1024).toFixed(1)} MB , the limit is 8 MB. Try the phone's "medium" size. Your current image is unchanged.`);
      return;
    }
    setBusy(true); setProgress(0); setLastFile(file);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("group", group); fd.append("alt", altDraft || "");
      const res = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      onChange(res.data.url, res.data);
      setMeta({ id: res.data.id, alt: res.data.alt || "", focal_x: 50, focal_y: 50 });
      setPreviewError(false);
      toast.success(`Uploaded ${res.data.filename} (${res.data.width}×${res.data.height}). Remember to save the page.`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload didn't finish (network). Your current image is unchanged , press Retry.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveMeta = async (patch) => {
    if (!meta?.id) return;
    try { const r = await api.patch(`/admin/media/${meta.id}`, patch); setMeta((m) => ({ ...m, ...r.data })); toast.success("Image details saved."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save image details."); }
  };

  const setFocal = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 100), y = Math.round(((e.clientY - r.top) / r.height) * 100);
    saveMeta({ focal_x: Math.max(0, Math.min(100, x)), focal_y: Math.max(0, Math.min(100, y)) });
  };
  const pos = meta ? `${meta.focal_x}% ${meta.focal_y}%` : "50% 50%";

  return (
    <div data-testid={testid} className="w-full">
      {label && <span className="block font-display tracking-[0.3em] text-xs uppercase text-[var(--dahlias-clay)] mb-2">{label}</span>}
      <div className="flex flex-col sm:flex-row items-stretch gap-4">
        <div className="w-full sm:w-44 h-32 bg-[repeating-conic-gradient(#f1ece0_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] border border-[var(--dahlias-ink)]/15 flex items-center justify-center overflow-hidden shrink-0 relative" data-testid={`${testid}-preview`}>
          {previewUrl && !previewError ? (
            <img src={previewUrl} alt={meta?.alt || "preview"} className="w-full h-full object-contain" onError={() => setPreviewError(true)} />
          ) : (
            <span className="text-[var(--dahlias-ink)]/40 flex flex-col items-center text-xs"><ImageIcon className="w-6 h-6 mb-1" />No image</span>
          )}
          {busy && <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/10"><div className="h-full bg-[var(--dahlias-sage-deep,#5A6B3E)] transition-[width]" style={{ width: `${progress}%` }} data-testid={`${testid}-progress`} /></div>}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`${testid}-button`}
              className="inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold bg-[var(--dahlias-ink,#3C2A1E)] text-white rounded disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : previewUrl ? <RefreshCw className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />}
              {busy ? `Uploading ${progress}%` : previewUrl ? "Replace image" : "Upload"}
            </button>
            <button type="button" onClick={() => setLibrary(true)} disabled={busy} data-testid={`${testid}-choose`}
              className="inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold border border-[var(--dahlias-ink,#3C2A1E)] text-[var(--dahlias-ink,#3C2A1E)] rounded disabled:opacity-50">
              <FolderOpen className="w-3.5 h-3.5" /> Choose existing
            </button>
            {previewUrl && <button type="button" onClick={() => { onChange(""); setMeta(null); setError(""); }} data-testid={`${testid}-clear`} className="inline-flex items-center gap-1 px-2 py-2 text-[11px] uppercase tracking-[0.18em] text-[var(--dahlias-ink)]/60"><X className="w-3.5 h-3.5" /> Remove</button>}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif,.heic,.heif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-input`} />
          {error && (
            <div className="text-xs text-[#A33030] bg-[#FFF4F0] border border-[#A33030]/40 rounded p-2 flex flex-wrap items-center gap-2" data-testid={`${testid}-error`}>
              <span className="flex-1 min-w-0">{error}</span>
              {lastFile && !HEIC_RX.test(lastFile.name) && lastFile.size <= MAX_BYTES && <button type="button" onClick={() => upload(lastFile)} data-testid={`${testid}-retry`} className="px-2 py-1 rounded bg-[#A33030] text-white text-[10px] uppercase tracking-[0.15em] font-semibold">Retry</button>}
            </div>
          )}
          <p className="text-[11px] text-[var(--dahlias-ink)]/55">PNG, JPEG, WebP or GIF up to 8 MB. Phone photos are rotated the right way up and resized to 1920px; transparent logos stay transparent. Uploading does not publish , save the page to use it.</p>
          {meta && (
            <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-center">
              <input value={altDraft} onChange={(e) => setAltDraft(e.target.value)} placeholder="Alt text (what's in the picture, for screen readers & search)" data-testid={`${testid}-alt`} className="h-8 px-2 text-xs border border-[var(--dahlias-ink)]/15 rounded" />
              <button type="button" onClick={() => saveMeta({ alt: altDraft })} data-testid={`${testid}-alt-save`} className="h-8 px-3 text-[10px] uppercase tracking-[0.15em] font-semibold border border-[var(--dahlias-ink)]/30 rounded">Save alt</button>
            </div>
          )}
        </div>
      </div>

      {showFocal && meta && previewUrl && !previewError && (
        <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-3 items-start" data-testid={`${testid}-focal`}>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--dahlias-clay)] mb-1">Focal point , click the picture where the subject is</div>
            <div className="relative w-full max-w-sm border border-[var(--dahlias-ink)]/15 cursor-crosshair bg-[repeating-conic-gradient(#f1ece0_0%_25%,#fff_0%_50%)] bg-[length:16px_16px]" onClick={setFocal} data-testid={`${testid}-focal-canvas`}>
              <img src={previewUrl} alt="" className="w-full block" />
              <span className="absolute w-4 h-4 -ml-2 -mt-2 rounded-full border-2 border-white shadow ring-2 ring-[#A33030]" style={{ left: `${meta.focal_x}%`, top: `${meta.focal_y}%` }} />
            </div>
          </div>
          <div className="flex gap-2 text-[10px] text-[var(--dahlias-ink)]/60 text-center">
            {[["Wide banner", "w-40 h-14"], ["Square card", "w-20 h-20"], ["Phone", "w-12 h-20"]].map(([l, cls]) => (
              <div key={l}><div className={`${cls} overflow-hidden border border-[var(--dahlias-ink)]/15 mx-auto`}><img src={previewUrl} alt="" className="w-full h-full object-cover" style={{ objectPosition: pos }} /></div>{l}</div>
            ))}
            <div><div className="w-20 h-20 rounded-full overflow-hidden border border-[var(--dahlias-ink)]/15 mx-auto"><img src={previewUrl} alt="" className="w-full h-full object-contain" /></div>Round (never stretched)</div>
          </div>
        </div>
      )}

      {library && <ImageLibraryModal onClose={() => setLibrary(false)} onPick={(m) => { onChange(m.url, m); setMeta({ id: m.id, alt: m.alt || "", focal_x: m.focal_x ?? 50, focal_y: m.focal_y ?? 50 }); setAltDraft(m.alt || ""); setPreviewError(false); setError(""); setLibrary(false); }} testid={`${testid}-library`} />}
    </div>
  );
}

export { MEDIA_GROUPS };
